import React, { useState } from "react";
import { FaPlus, FaRegCopy } from "react-icons/fa";
import { TextInput } from "../atoms";
import { useCreateTaskMutation } from "../../store";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import StatusBadge from "./atoms/StatusBadge";
import AssigneeChips from "./AssigneeChips";
import useQuickAssign from "./useQuickAssign";
import { useDirtyDraft } from "./nestedOverlay";

// İş detayı "Alt işler" bölümü. Contract 1.1: TaskDetail.subtasks (SubtaskRef[]),
// create { project, parent, title }. Tek seviye: alt işin alt işi olmaz.
// Satırdaki kopya düğmesi aynı üst işe "<başlık> - N" kopyası açar.

export const NO_ACCESS_TOOLTIP = "Bu işi görme yetkiniz yok";
const errText = (e, fallback) => e?.data?.message || e?.message || fallback;
const MAX_TITLE = 200;

/**
 * Kopya başlığı: "Montaj" → "Montaj - 2"; "Montaj - 2" → sıradaki boş numara
 * ("Montaj - 3"). Kardeşlerde kullanılmış numaralar atlanır; 200 karakteri aşmaz.
 */
export function copyTitle(title, siblingTitles = []) {
  const m = String(title || "").trim().match(/^(.*?) - (\d+)$/);
  const base = (m ? m[1] : String(title || "").trim()) || "Alt iş";
  const taken = new Set(siblingTitles.map((x) => String(x || "").trim()));
  let n = 2;
  const make = (k) => {
    const suffix = ` - ${k}`;
    return base.slice(0, MAX_TITLE - suffix.length) + suffix;
  };
  while (taken.has(make(n))) n += 1;
  return make(n);
}

// 1.8 — atananlar satırda: canAssign ise × / + ile alt işi açmadan değişir.
function SubtaskRow({ sub, onOpen, onCopy, copying, onAssign }) {
  const head = (
    <>
      <span className="text-xs text-gray-500 font-Medium shrink-0">{sub.key}</span>
      <span className="flex-1 min-w-0 text-sm text-gray-800 truncate">{sub.title}</span>
      {sub.archivedAt && (
        <span className="shrink-0 text-[10px] font-Medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">Arşivde</span>
      )}
      <StatusBadge status={sub.status} />
    </>
  );
  if (!sub.canOpen) {
    return (
      <li
        data-testid="subtask-row"
        data-disabled="true"
        title={NO_ACCESS_TOOLTIP}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg opacity-50 cursor-not-allowed"
      >
        {head}
      </li>
    );
  }
  const assignees = sub.assignees || [];
  return (
    <li data-testid="subtask-row" className="flex flex-wrap items-center gap-x-1 gap-y-1 pr-1">
      <button
        type="button"
        onClick={() => onOpen && onOpen(sub.key)}
        className="flex-1 min-w-[10rem] flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-gray-50"
      >
        {head}
      </button>
      {(assignees.length > 0 || sub.canAssign) && (
        <div className="shrink-0 pl-2">
          <AssigneeChips
            users={assignees}
            canEdit={Boolean(sub.canAssign && onAssign)}
            onChange={(ids) => onAssign(sub, ids)}
            emptyText=""
          />
        </div>
      )}
      {onCopy && (
        <button
          type="button"
          onClick={() => onCopy(sub)}
          disabled={copying}
          aria-label={`${sub.key} kopyala`}
          title="Kopyasını oluştur"
          className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-40"
        >
          <FaRegCopy className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

function TaskSubtasks({ task, onOpenTask }) {
  const { auth } = useAuth();
  const { showError } = useAlert();
  const [createTask, { isLoading: creating }] = useCreateTaskMutation();
  const quickAssign = useQuickAssign();
  const [draft, setDraft] = useState(null);
  useDirtyDraft(draft !== null && draft.trim() !== "");

  const subtasks = task.subtasks || [];
  const canAdd = Boolean(task.can?.edit) && !task.parent;
  const doneCount = subtasks.filter((s) => s.status === "done").length;

  const submit = async () => {
    const title = (draft || "").trim();
    if (!title || creating) return;
    if (title.length > 200) {
      showError("Başlık 1–200 karakter olmalı.");
      return;
    }
    try {
      await createTask({
        token: auth?.accessToken,
        project: task.project?._id,
        parent: task._id,
        title,
      }).unwrap();
      setDraft(null);
    } catch (e) {
      showError(errText(e, "Alt iş eklenemedi."));
    }
  };

  // Kopya: aynı üst işin altına "<başlık> - N" adıyla, atananlarıyla birlikte; içeriği
  // kullanıcı düzenler — oluşan kopya hemen açılır.
  const copy = async (sub) => {
    if (creating) return;
    try {
      const res = await createTask({
        token: auth?.accessToken,
        project: task.project?._id,
        parent: task._id,
        title: copyTitle(sub.title, subtasks.map((x) => x.title)),
        ...((sub.assignees || []).length ? { assignees: sub.assignees.map((u) => u._id) } : {}),
      }).unwrap();
      const created = res?.data?.task;
      if (created?.key && onOpenTask) onOpenTask(created.key);
    } catch (e) {
      showError(errText(e, "Alt iş kopyalanamadı."));
    }
  };

  if (!subtasks.length && !canAdd) return null;

  return (
    <section className="space-y-2" data-testid="task-subtasks">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-SemiBold text-gray-700">
          Alt işler {subtasks.length > 0 && `(${doneCount}/${subtasks.length})`}
        </h3>
        {canAdd && draft === null && (
          <button
            type="button"
            onClick={() => setDraft("")}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-Medium text-gray-600 hover:bg-gray-100"
          >
            <FaPlus className="w-3 h-3" aria-hidden="true" />
            Alt iş
          </button>
        )}
      </div>
      {subtasks.length > 0 && (
        <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100">
          {subtasks.map((s) => (
            <SubtaskRow
              key={s._id}
              sub={s}
              onOpen={onOpenTask}
              onCopy={canAdd ? copy : null}
              copying={creating}
              onAssign={(sub, ids) => quickAssign(sub, ids, task._id)}
            />
          ))}
        </ul>
      )}
      {draft !== null && (
        <TextInput
          autoFocus
          aria-label="Alt iş başlığı"
          placeholder="Alt iş başlığı ve Enter"
          value={draft}
          maxLength={200}
          disabled={creating}
          className="!py-1.5 text-sm"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (!draft.trim() && !creating) setDraft(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation(); // çekmecenin Esc kapatmasına gitmesin
              setDraft(null);
            }
          }}
        />
      )}
    </section>
  );
}

export function ParentBreadcrumb({ parent, onOpenTask }) {
  if (!parent) return null;
  return (
    <button
      type="button"
      data-testid="task-parent-link"
      onClick={() => onOpenTask && onOpenTask(parent.key)}
      className="inline-flex items-center gap-1 max-w-full text-xs text-gray-500 hover:text-main"
    >
      <span aria-hidden="true">↳</span>
      <span className="font-Medium shrink-0">{parent.key}</span>
      <span className="truncate">· {parent.title}</span>
    </button>
  );
}

export default TaskSubtasks;
